import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Form, Grid, Input, Button, Message, Typography  } from "@arco-design/web-react";

import { endpoints } from "../api/endpoints";
import { Logo } from "../components/Logo";
import { LanguageSelect } from "../components/LanguageSelect/LanguageSelect";
import { routes } from "../routes/routes";
import { clearAuthSession } from "../utils/clearAuthSession";
import { getBoxBackgroundColor, getButtonColor, getButtonTextColor, getColorAccent1, getTextColor } from "../utils/cssVariables";
import { getAuthLogoType } from "../theme";

const Row = Grid.Row;
const FormItem = Form.Item;

const PasswordComplexityTooltip = () => {
  const { t } = useTranslation();

  return <div>
    <Typography.Paragraph
        style={{ fontSize: 12, color: "white", marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        spacing="close"
    >
      {t('password.password_requirements')}
      <ul>
        <li>{t('password.password_lowercase')}</li>
        <li>{t('password.password_uppercase')}</li>
        <li>{t('password.password_digit')}</li>
        <li>{t('password.password_special')}</li>
      </ul>
    </Typography.Paragraph>
  </div>
}

export const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    const email = params.get("email");
    const otp = params.get("otp");
    return { email, otp };
  };

  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);

  useEffect(() => {
    clearAuthSession();
  }, []);

  const sendEmailWithOtpCode = (formEmail: string | undefined = undefined) => {
    axios
      .post(endpoints.recoverAccount, {
        "email": form.getFieldValue("email"),
      })
      .then(() => {
        if (!formEmail) {
          Message.success({ content: t('notification_messages.link_resent_success'), closable: true, duration: 3000});
        }
      })
      .catch((error) => {
        console.error(error);
        if (error.response?.status === 404) {
          Message.success({ content: t('notification_messages.link_resent_success'), closable: true, duration: 4000 });
          return;
        }
        Message.error(t('notification_messages.link_resent_error'));
      })
  }

  const onSubmit = async () => {
    if (!isEmailSent) {
      setIsEmailSent(true);
      form.validate().then(sendEmailWithOtpCode);
    }
  };

  useEffect(() => {
    const { email, otp } = getQueryParams();

    if (email && otp) {
      setIsEmailSent(true);
      form.setFieldValue("otp", otp);
      form.setFieldValue("email", email);
    }
  }, []);  

  return (
      <div
        style={{
          justifyContent: "center",
          backgroundColor: getBoxBackgroundColor(),
          maxWidth: 480,
          width: "calc(100% - 40px)",
          margin: "20px auto",
          padding: "20px",
          borderRadius: 8,
        }}
      >
        <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
        <div style={{
            display: 'flex',
            justifyContent: "center",
            paddingTop: 12,
            margin: "0 auto",
        }}><LanguageSelect color={getTextColor()}/></div>
        <Form onSubmit={onSubmit} form={form}>
          <Row justify="center">
            <div
              style={{
                padding: "20px",
                width: "calc(100% - 40px)",
                maxWidth: 350,
              }}
            >
              <div style={{marginTop: 10}}>
                <Typography.Text
                  style={{fontSize: 20, color: getColorAccent1(), wordBreak: "break-word"}}
                  type="success"
                  className={"alice-regular"}
                >
                {!isEmailSent ? t('reset_password_page.trouble_logging_in') : t('reset_password_page.reset_password')}
                </Typography.Text>
              </div>
              <div style={{marginTop: 30}}>
                {!isEmailSent ? (
                  <Typography.Paragraph style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {t('reset_password_page.enter_email_for_reset')}
                  </Typography.Paragraph>
                ) : (
                  <Typography.Paragraph style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {t('reset_password_page.link_sent_message')}
                    <br/>
                  </Typography.Paragraph>
                )}
              </div>
              <div style={{marginTop: 20}}>
                {<FormItem
                  layout="vertical"
                  label={t('reset_password_page.email_label')}
                  field="email"
                  disabled={isEmailSent}
                  rules={!isEmailSent ? [
                    {
                      validator: (v, cb) => {
                        return !v ? cb(t('reset_password_page.email_required'))
                          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? cb(t('reset_password_page.invalid_email'))
                          : cb();
                      }
                    }
                  ] : []}
                  validateTrigger="onBlur"
                >
                  <Input
                    type="email"
                    placeholder={t('reset_password_page.email_placeholder')}
                  />
                </FormItem>}
                {!isEmailSent && <Button
                    htmlType="submit"
                    style={{
                      color: getButtonTextColor(),
                      backgroundColor: getButtonColor(),
                      width: "100%",
                      marginTop: 20,
                    }}
                    size={"large"}
                    shape={"round"}
                  >
                  {t('reset_password_page.send_email_button')}
                  </Button>}
                {isEmailSent && <>
                <Typography.Paragraph style={{ marginTop: 30, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{t('reset_password_page.after_reset_message')}</Typography.Paragraph>

                    <FormItem layout="vertical" style={{ marginTop: 10, marginBottom: 0 }}>
                      <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8,}}>
                        <Button
                          onClick={() => {
                            form.resetFields();
                            setIsEmailSent(false);
                          }}
                          size={"large"}
                          shape={"round"}
                        >
                          {t('reset_password_page.use_different_email')}
                        </Button>
                        <Button
                          htmlType="submit"
                          style={{
                            color: getButtonTextColor(),
                            backgroundColor: getButtonColor()
                          }}
                          size={"large"}
                          shape={"round"}
                          onClick={() => navigate(routes.LOGIN)}
                        >
                          {t('reset_password_page.back_to_login')}
                        </Button>
                      </Row>
                    </FormItem>
                </>}
              </div>
            </div>
          </Row>
        </Form>

        <div style={{textAlign: "center", marginTop: 20}}>
          <Typography.Paragraph>
            {t('reset_password_page.remember_credentials')}
            <NavLink to={routes.LOGIN}>
              <Button
                  style={{padding: "0 0 0 4px"}}
                  type="text"
                  status="success"
              >
              <span style={{ color: getColorAccent1() }}>{t('reset_password_page.back_to_login')}</span>
            </Button>
          </NavLink>
        </Typography.Paragraph>
      </div>
    </div>
  );
};
